/**
 * React Query hooks.
 *
 * POLLING IS DRIVEN BY THE RESOLVED STATE, not by the stored status. A run whose
 * heartbeat went quiet resolves to `timed_out` and stops polling immediately —
 * otherwise the client would keep asking a dead worker for an answer, forever,
 * at a fixed interval.
 *
 * Every mutation invalidates EXACT keys. Reports are deliberately not among them:
 * a new context, assumption or thesis version makes an existing report stale, not
 * wrong, and refreshing is a founder's explicit decision rather than a cache side
 * effect that silently spends a generation credit.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { isMockModeActive } from '@/lib/state/mock-data-provider';
import {
  fetchMethodologyManifest,
  fetchInputFingerprints,
  fetchStrategyContextDraft,
  requestGeneration,
  saveExitThesis,
  saveMarketAssumptions,
  saveStrategyContext,
} from './api';
import {
  keysAfterAssumptionsSave,
  keysAfterContextSave,
  keysAfterThesisSave,
  strategicAnalysisKeys,
} from './query-keys';
import { resolveStrategicRunState } from './status';
import { parseReportForDisplay } from './report-contract';
import { resolveReportView } from './report-state';
import {
  fetchLastReadyRun,
  fetchRunEvidence,
  fetchStartupName,
  fetchStrategicRunRow,
  fetchStrategicRunStates,
  type StoredStrategicRun,
} from './run-queries';
import { deriveEntryState, type EntryState } from './entry-state';
import {
  toBoundVersionIds,
  toClaimViews,
  toCoverageView,
  toDerivation,
  toEvidenceViews,
  toSnapshot,
} from './report-view';
import type {
  AnalysisType,
  ExitThesis,
  MarketAssumptions,
} from './types';

/** The latest run and the last one that published, read together. */
interface StrategicRunPair {
  latest: StoredStrategicRun | null;
  lastReady: StoredStrategicRun | null;
}

/** Cheap enough to poll, slow enough not to hammer a background job. */
const POLL_INTERVAL_MS = 5_000;

/**
 * The latest run for one analysis, with its state resolved at read time.
 *
 * `enabled` gates on mock mode as well as the ids: a demo must not open a
 * network connection, and an unauthenticated or flag-disabled screen has nothing
 * to fetch.
 */
export function useStrategicAnalysis(
  startupId: string | undefined,
  analysisType: AnalysisType,
  options: { currentInputFingerprint?: string; enabled?: boolean } = {},
) {
  const enabled = Boolean(startupId) && options.enabled !== false && !isMockModeActive();

  return useQuery({
    queryKey: strategicAnalysisKeys.run(startupId ?? 'none', analysisType),
    enabled,
    // Bound and inspected inside: an unbound result here would render "no
    // analysis" for a read failure, which is indistinguishable from "never
    // generated".
    // The last READY run is fetched alongside the latest one so a refresh does
    // not blank the report a reader is already looking at.
    queryFn: async () => {
      const [latest, lastReady] = await Promise.all([
        fetchStrategicRunRow(startupId!, analysisType),
        fetchLastReadyRun(startupId!, analysisType),
      ]);
      return { latest, lastReady };
    },
    select: ({ latest, lastReady }: StrategicRunPair) => {
      const resolved = resolveStrategicRunState(latest, Date.now(), {
        currentInputFingerprint: options.currentInputFingerprint,
      });

      // ONE SELECTION, NOT TWO.
      //
      // The hook used to choose a run twice: `selectVisibleReport` picked the row
      // whose coverage, derivation, snapshot and evidence would caption the
      // report, and `resolveReportView` independently picked the row whose body
      // would be drawn. Nothing made them agree. During a refresh they diverged
      // by construction — the previous report's body under the new run's
      // coverage, which is a limitations band describing a search that produced
      // none of what is on screen.
      //
      // The view now names the run it selected, and every caption is read from
      // that row.
      const view = resolveReportView({
        analysisType,
        isLoading: false,
        resolved,
        latest,
        lastReady,
      });

      const displayedRunId = view.kind === 'ready' || view.kind === 'legacy_report'
        ? view.displayedRunId
        : null;
      const source = displayedRunId === null
        ? null
        : [latest, lastReady].find((row) => row?.id === displayedRunId) ?? null;

      return {
        ...resolved,
        view,
        /** The run the DISPLAYED report belongs to — what its evidence hangs off. */
        displayedRunId,
        /** The contract version the DISPLAYED report was published under. */
        schemaVersion: source?.schema_version ?? null,
        showingPrevious: view.kind === 'ready' && view.showingPrevious,
        /**
         * WHEN THE NEWEST PUBLISHED ANALYSIS LANDED — read from `lastReady`, not
         * from the displayed run, so it SURVIVES a refresh that published
         * nothing. `displayedRunId` is null for every state except `ready` and
         * `legacy_report`, and every other caption hangs off it, so a failed
         * refresh used to take the screen's memory of what the founder already
         * had with it.
         */
        lastPublishedAt: lastReady?.updated_at ?? null,
        /**
         * THE ANALYSIS THE FOUNDER STILL HAS, when the screen is showing a run
         * outcome instead of a report.
         *
         * Only ever a LEGACY body. A readable previous report already becomes
         * the view itself through `displayOr` (`showingPrevious`), so surfacing
         * it here too would draw it twice. A pre-contract one cannot become the
         * view — the outcome of the newer run outranks a notice about the older
         * one — and without this it vanished from the screen entirely, which is
         * the complaint that started all of this: "something should be displayed
         * but older, since there were previous analyses done".
         */
        previousLegacyReport: (() => {
          if (!lastReady || lastReady.report === null || lastReady.report === undefined) return null;
          // Already on screen as the view; nothing to add.
          if (view.kind === 'ready' || view.kind === 'legacy_report') return null;
          const parsed = parseReportForDisplay(
            analysisType,
            lastReady.schema_version,
            lastReady.report,
          );
          return parsed.status === 'legacy' ? parsed.report : null;
        })(),
        coverage: source ? toCoverageView(source.research_coverage, source.provider_warnings) : null,
        derivation: source ? toDerivation(source.derivation) : null,
        snapshot: source ? toSnapshot(source) : null,
        /** Ids of the exact assumption/thesis rows THAT report read. */
        boundVersionIds: source
          ? toBoundVersionIds(source)
          : { strategyContextVersionId: null, marketAssumptionVersionId: null, exitThesisVersionId: null },
      };
    },
    refetchInterval: (query) => {
      const resolved = query.state.data
        ? resolveStrategicRunState((query.state.data as StrategicRunPair).latest, Date.now(), {
          currentInputFingerprint: options.currentInputFingerprint,
        })
        : null;
      // Only a live, non-terminal run polls. A timed-out one has already been
      // resolved and asking again cannot change the answer.
      return resolved?.shouldPoll ? POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Claims and retained sources for one run.
 *
 * Keyed on the RUN id, not the analysis type: a run is immutable once published,
 * so its evidence never changes and a refresh that produces a new run gets a new
 * cache entry rather than mutating the one an investor may still be reading.
 *
 * `enabled` gates on the run id, so nothing is fetched until there is a run to
 * fetch it for.
 */
export function useRunEvidence(startupId: string | undefined, runId: string | null | undefined) {
  return useQuery({
    queryKey: strategicAnalysisKeys.evidence(startupId ?? 'none', runId ?? 'none'),
    enabled: Boolean(startupId && runId) && !isMockModeActive(),
    // Immutable once published; no reason to refetch it on focus.
    staleTime: 60 * 60 * 1000,
    queryFn: async () => await fetchRunEvidence(startupId!, runId!),
    select: (evidence) => ({
      claims: toClaimViews(evidence.claims, evidence.claimSources, evidence.sources),
      sources: toEvidenceViews(evidence.sources),
    }),
  });
}

/**
 * The EXACT market-assumption version a run read.
 *
 * Never "the latest": a report states what it concluded from the inputs it
 * names, and pairing it with a newer set of assumptions would caption a
 * published calculation with numbers it never used.
 */
export function useBoundMarketAssumptions(
  startupId: string | undefined,
  versionId: string | null | undefined,
) {
  return useQuery({
    queryKey: strategicAnalysisKeys.marketAssumptions(startupId ?? 'none', versionId ?? 'none'),
    enabled: Boolean(startupId && versionId) && !isMockModeActive(),
    // An immutable version row. Nothing about it can change.
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_market_assumptions')
        .select('assumptions')
        .eq('id', versionId!)
        .eq('startup_id', startupId!)
        .maybeSingle();
      if (error) throw error;
      // The whole client object lives in the jsonb column as written.
      return (data?.assumptions ?? null) as MarketAssumptions | null;
    },
  });
}

/**
 * The LATEST assumptions, which is the set a founder edits.
 *
 * Same separation as the thesis below: a published market report states what it
 * computed from the version it BOUND, and that must keep showing beside the
 * independent estimate after the founder revises. Reads the `'latest'` key that
 * `keysAfterAssumptionsSave` already invalidates.
 */
export function useLatestMarketAssumptions(
  startupId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: strategicAnalysisKeys.marketAssumptions(startupId ?? 'none', 'latest'),
    enabled: Boolean(startupId) && options.enabled !== false && !isMockModeActive(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_market_assumptions')
        .select('assumptions')
        .eq('startup_id', startupId!)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data?.assumptions ?? null) as MarketAssumptions | null;
    },
  });
}

/** The EXACT thesis version the assessment read. Same rule as above. */
export function useBoundExitThesis(
  startupId: string | undefined,
  versionId: string | null | undefined,
) {
  return useQuery({
    queryKey: strategicAnalysisKeys.exitThesis(startupId ?? 'none', versionId ?? 'none'),
    enabled: Boolean(startupId && versionId) && !isMockModeActive(),
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_exit_theses')
        // ONE string literal, deliberately. supabase-js infers the row type by
        // parsing this at the type level, and a concatenated string is not a
        // literal it can read — the whole row degrades to GenericStringError and
        // every property access below stops typechecking.
        .select('id, version, created_at, preferred_pathways, open_to_assessment, horizon_earliest_year, horizon_latest_year, target_valuation_low, target_valuation_high, target_valuation_currency, buyer_characteristics, named_candidates, rationale, assumptions, founder_source_urls')
        .eq('id', versionId!)
        .eq('startup_id', startupId!)
        .maybeSingle();
      if (error) throw error;
      return toExitThesis(data);
    },
  });
}

/**
 * The LATEST thesis, which is the one a founder edits.
 *
 * Deliberately separate from `useBoundExitThesis`. A report is a statement about
 * the exact version it assessed, and that version must keep showing beside the
 * assessment however many times the founder revises afterwards — so the editor
 * cannot reuse the bound query without silently rewriting history on screen.
 * Reads the `'latest'` key that `keysAfterThesisSave` already invalidates.
 *
 * Founder-only by intent. RLS is what enforces it; `enabled` only avoids a
 * request an investor would be refused anyway.
 */
export function useLatestExitThesis(
  startupId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: strategicAnalysisKeys.exitThesis(startupId ?? 'none', 'latest'),
    enabled: Boolean(startupId) && options.enabled !== false && !isMockModeActive(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_exit_theses')
        .select('id, version, created_at, preferred_pathways, open_to_assessment, horizon_earliest_year, horizon_latest_year, target_valuation_low, target_valuation_high, target_valuation_currency, buyer_characteristics, named_candidates, rationale, assumptions, founder_source_urls')
        .eq('startup_id', startupId!)
        // Highest VERSION, not newest created_at: version is the ordering the
        // save path assigns and the only one that cannot be reordered by a clock.
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return toExitThesis(data);
    },
  });
}

/** One row shape, one client type. Shared so bound and latest cannot drift. */
type ExitThesisRow = {
  id: string;
  version: number;
  created_at: string;
  preferred_pathways: string[] | null;
  open_to_assessment: boolean | null;
  horizon_earliest_year: number | null;
  horizon_latest_year: number | null;
  target_valuation_low: number | null;
  target_valuation_high: number | null;
  target_valuation_currency: string | null;
  buyer_characteristics: string[] | null;
  named_candidates: unknown;
  rationale: string | null;
  assumptions: string[] | null;
  founder_source_urls: string[] | null;
};

function toExitThesis(data: ExitThesisRow | null): ExitThesis | null {
  if (!data) return null;
  // Explicit columns rather than one blob, so this is the one place the row
  // shape is turned back into the client type.
  return {
    id: data.id,
    version: data.version,
    createdAt: data.created_at,
    preferredPathways: data.preferred_pathways ?? [],
    openToAssessment: data.open_to_assessment ?? false,
    horizonEarliestYear: data.horizon_earliest_year ?? undefined,
    horizonLatestYear: data.horizon_latest_year ?? undefined,
    targetValuationLow: data.target_valuation_low ?? undefined,
    targetValuationHigh: data.target_valuation_high ?? undefined,
    targetValuationCurrency: data.target_valuation_currency ?? undefined,
    buyerCharacteristics: data.buyer_characteristics ?? [],
    namedCandidates: data.named_candidates ?? [],
    rationale: data.rationale ?? undefined,
    assumptions: data.assumptions ?? [],
    founderSourceUrls: data.founder_source_urls ?? [],
  } as ExitThesis;
}

/**
 * Today's input fingerprints, per analysis type.
 *
 * A report is stale exactly when the fingerprint stored on its run differs from
 * the one here. Founder-only: every startup-scoped action on the function is
 * behind the membership gate, and the entry card already collapses an investor
 * to "there is a report or there is not".
 */
export function useInputFingerprints(
  startupId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
       queryKey: strategicAnalysisKeys.inputFingerprints(startupId ?? 'none'),
    enabled: Boolean(startupId) && options.enabled !== false && !isMockModeActive(),
    queryFn: async () => (await fetchInputFingerprints(startupId!)).fingerprints,
  });
}

/**
 * Everything the profile entry card needs, as one object to spread.
 *
 * Spread rather than passed field by field on purpose: both call sites
 * previously hardcoded `hasReport={false}` and omitted the rest, and a single
 * object is much harder to freeze by accident than five separate props.
 *
 * The draft is fetched only for a member — it is a founder-only endpoint, and an
 * investor firing it would get a refusal for a request that should never have
 * been made.
 */
export function useStrategicEntryState(
  startupId: string | undefined,
  options: { canEdit: boolean; enabled?: boolean },
): EntryState {
  const enabled = Boolean(startupId) && options.enabled !== false && !isMockModeActive();

  const runs = useQuery({
       queryKey: strategicAnalysisKeys.runStates(startupId ?? 'none'),
    enabled,
    queryFn: async () => await fetchStrategicRunStates(startupId!),
  });

  const draft = useStrategyContextDraft(startupId, { enabled: enabled && options.canEdit });
  // Founder-only, like the draft: the action is behind the membership gate and
  // an investor's card has no stale state to show anyway.
  const fingerprints = useInputFingerprints(startupId, { enabled: enabled && options.canEdit });

  return useMemo(
    () => deriveEntryState({
      runs: runs.data,
      draft: draft.data?.draft,
      canEdit: options.canEdit,
      fingerprints: fingerprints.data,
    }),
    [runs.data, draft.data, options.canEdit, fingerprints.data],
  );
}

export function useMethodologyManifest(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: strategicAnalysisKeys.manifest(),
    enabled: options.enabled !== false && !isMockModeActive(),
    // The methodology changes only with a deployment, so it is worth caching
    // hard — every screen needs it and none of them need it fresh.
    staleTime: 60 * 60 * 1000,
    queryFn: fetchMethodologyManifest,
  });
}

/**
 * The server-assembled candidate draft.
 *
 * FOUNDER-ONLY: it carries candidate provenance, and the endpoint refuses it to
 * anyone without active team membership. `enabled` should be false for an
 * investor view — not as the boundary, which is server-side, but so the screen
 * does not fire a request it knows will be denied.
 */
export function useStrategyContextDraft(
  startupId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: strategicAnalysisKeys.contextDraft(startupId ?? 'none'),
    enabled: Boolean(startupId) && options.enabled !== false && !isMockModeActive(),
    // THE WHOLE RESPONSE. Taking `.draft` here discarded `boundVersions`, which
    // is the only place the client learns which assumption and thesis version to
    // bind — so Generate had nothing to send and two of the five analyses were
    // unreachable.
    queryFn: async () => await fetchStrategyContextDraft(startupId!),
  });
}

/** The exact approved version a report was bound to. Never "the latest". */
export function useStrategyContext(startupId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey: strategicAnalysisKeys.contextVersion(startupId ?? 'none', versionId ?? 'none'),
    enabled: Boolean(startupId && versionId) && !isMockModeActive(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_strategy_contexts')
        .select('id, version, context, created_at')
        .eq('id', versionId!)
        .eq('startup_id', startupId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useSaveStrategyContext(startupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Parameters<typeof saveStrategyContext>[0], 'startupId'>) =>
      saveStrategyContext({ startupId, ...input }),
    onSuccess: () => {
      for (const key of keysAfterContextSave(startupId)) {
        client.invalidateQueries({ queryKey: key });
      }
      // Reports are NOT invalidated. The new version marks them stale; the
      // founder decides whether to spend a credit refreshing.
    },
  });
}

export function useSaveMarketAssumptions(startupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (assumptions: unknown) => saveMarketAssumptions(startupId, assumptions),
    onSuccess: () => {
      for (const key of keysAfterAssumptionsSave(startupId)) {
        client.invalidateQueries({ queryKey: key });
      }
    },
  });
}

export function useSaveExitThesis(startupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (thesis: unknown) => saveExitThesis(startupId, thesis),
    onSuccess: () => {
      for (const key of keysAfterThesisSave(startupId)) {
        client.invalidateQueries({ queryKey: key });
      }
      // The last ready assessment stays visible with a stale banner. Saving a
      // thesis does not auto-generate and does not spend a credit.
    },
  });
}

/**
 * Start every analysis a founder is actually allowed to start, in one action.
 *
 * SEQUENTIAL, AND EACH OUTCOME IS KEPT. Five parallel invocations would spend
 * five research budgets at once and, more importantly, one refusal would lose
 * the rest — `Promise.all` rejects on the first. Each request is awaited and its
 * outcome recorded against its analysis, so the caller can say what started and
 * what did not. That is the opposite of catch-and-continue: nothing is
 * swallowed, every failure is returned and surfaced.
 *
 * The requests return as soon as the server accepts them (202) — the research
 * runs in its own isolate — so this is five quick round trips, not five waits.
 */
export function useRefreshAllStrategicAnalyses(startupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      entries: readonly {
        analysisType: AnalysisType;
        strategyContextVersionId: string;
        /** Present only for the analysis that binds one. */
        boundVersionId: string | null;
      }[],
    ) => {
      const results: { analysisType: AnalysisType; started: boolean; error?: unknown }[] = [];
      for (const entry of entries) {
        try {
          await requestGeneration({
            startupId,
            analysisType: entry.analysisType,
            strategyContextVersionId: entry.strategyContextVersionId,
            // THE EXACT ID, and only for the analysis that reads it. Sending one
            // to an analysis that binds nothing would attach a version its
            // report never reads.
            ...(entry.analysisType === 'market_validation' && entry.boundVersionId
              ? { assumptionVersionId: entry.boundVersionId }
              : {}),
            ...(entry.analysisType === 'exit_strategy' && entry.boundVersionId
              ? { exitThesisVersionId: entry.boundVersionId }
              : {}),
          });
          results.push({ analysisType: entry.analysisType, started: true });
        } catch (error) {
          results.push({ analysisType: entry.analysisType, started: false, error });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      // Only what actually started. Invalidating a refused analysis would refetch
      // a run that never began.
      for (const result of results) {
        if (!result.started) continue;
        client.invalidateQueries({
          queryKey: strategicAnalysisKeys.run(startupId, result.analysisType),
        });
      }
    },
  });
}

export function useGenerateStrategicAnalysis(startupId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Parameters<typeof requestGeneration>[0], 'startupId'>) =>
      requestGeneration({ startupId, ...input }),
    onSuccess: (_data, variables) => {
      // Invalidate only the analysis that was requested, so the other four keep
      // their cached reports instead of all five refetching.
      client.invalidateQueries({
        queryKey: strategicAnalysisKeys.run(startupId, variables.analysisType),
      });
    },
  });
}

/**
 * The analysed company's name, for the header and the positioning map.
 *
 * Nothing on this screen depends on it: an absent name degrades to the generic
 * "Your startup" marker rather than blocking the report, which is what keeps a
 * failed or forbidden profile read from taking the analysis down with it.
 */
export function useStartupName(startupId: string | undefined) {
  return useQuery({
    queryKey: [...strategicAnalysisKeys.startup(startupId ?? 'none'), 'name'] as const,
    enabled: Boolean(startupId) && !isMockModeActive(),
    // A company name is not a live value.
    staleTime: 60 * 60 * 1000,
    queryFn: async () => await fetchStartupName(startupId!),
  });
}
